import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { camerasApi } from '@api/cameras';
import { HttpError } from '@api/http';
import type { Camera, CameraListQuery, PaginatedResult } from '@api/types';

interface UseCamerasState {
  data: PaginatedResult<Camera> | null;
  isLoading: boolean;
  error: string | null;
}

interface UseCamerasResult extends UseCamerasState {
  refresh: () => void;
  setOptimistic: (updater: (prev: PaginatedResult<Camera>) => PaginatedResult<Camera>) => void;
}

export function useCameras(query: CameraListQuery): UseCamerasResult {
  const [state, setState] = useState<UseCamerasState>({
    data: null,
    isLoading: true,
    error: null,
  });

  const queryKey = JSON.stringify(query);
  const reloadCount = useRef(0);

  const refresh = useCallback(() => {
    reloadCount.current += 1;
    setState((s) => ({ ...s, isLoading: true }));
  }, []);

  const setOptimistic = useCallback(
    (updater: (prev: PaginatedResult<Camera>) => PaginatedResult<Camera>) => {
      setState((s) => (s.data ? { ...s, data: updater(s.data) } : s));
    },
    [],
  );

  useEffect(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, isLoading: true, error: null }));

    camerasApi
      .list(query, controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        setState({ data, isLoading: false, error: null });
      })
      .catch((err: unknown) => {
        if (axios.isCancel(err) || controller.signal.aborted) return;
        const message =
          err instanceof HttpError ? err.message : err instanceof Error ? err.message : 'Failed to load cameras';
        setState((s) => ({ data: s.data, isLoading: false, error: message }));
      });

    return () => controller.abort();
    // queryKey + reloadCount.current intentionally drive refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, reloadCount.current]);

  return { ...state, refresh, setOptimistic };
}
